/**
 * Loads an image URL and returns a base64 PNG data URI, letterboxed onto a
 * canvas sized to the project's current preview dimensions with a white
 * backdrop. Built for AI Scenes' per-scene product image (POR-13874-2):
 * VideoTable.vue's dispatchAiSceneClips sends the result straight to
 * startAiMotion's `image` field.
 *
 * Why letterbox to the preview size instead of sending the source file as-is:
 *  - Veo generates at the width/height sent alongside the image. Handing it
 *    an image already in that exact aspect ratio leaves nothing to reframe —
 *    and AiMotionPresets::FIDELITY_RULE (the rule that takes effect whenever
 *    an image is supplied) explicitly forbids Veo from reframing or cropping
 *    the product, so a mismatched ratio would put the prompt in direct
 *    conflict with the input it was handed.
 *  - White matches TYPE_SUBSCENE's Scene_Background default (draft-scenes.js,
 *    "#FFFFFF"), so the letterbox bars read as the scene would inside the
 *    editor rather than as an arbitrary colour choice.
 *  - It also bounds the payload well under StoreAiMotionRequest's ~12MB
 *    base64 ceiling regardless of the source file's own size — a
 *    preview-sized canvas caps the pixel count no matter what gets loaded
 *    onto it.
 *
 * Rejects — never resolves with a partial or blank frame — if the source
 * fails to load or the canvas can't be read back (e.g. a cross-origin source
 * without permissive CORS headers taints the canvas and toDataURL throws).
 * Callers must treat a rejection as "no image" and fall back to
 * text-to-video rather than risk sending a broken frame into a paid Veo
 * generation.
 */
export const letterboxImage = (url, previewSize) => new Promise((resolve, reject) => {
  const img = new Image();

  // Script images can come from S3 (Files/Products/Stock pickers in
  // SelectFolderModal), not just this app's own /video/upload_file endpoint —
  // anonymous CORS matches the pattern already used elsewhere for
  // canvas-then-export flows (utils.js, lasso-tool/index.js) so toDataURL
  // below doesn't throw on a tainted canvas.
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = previewSize.width;
      canvas.height = previewSize.height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // "contain" fit: scale down (or up) to the largest size that still
      // fits both axes, then centre it — this is the letterbox/pillarbox.
      const scale = Math.min(
        canvas.width / img.naturalWidth,
        canvas.height / img.naturalHeight
      );
      const drawWidth = img.naturalWidth * scale;
      const drawHeight = img.naturalHeight * scale;
      const dx = (canvas.width - drawWidth) / 2;
      const dy = (canvas.height - drawHeight) / 2;

      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);

      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    }
  };

  img.onerror = () => reject(new Error('Image failed to load.'));

  img.src = url;
});
